import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, Pencil, Trash2, Printer, Send, CheckCircle2, XCircle, Receipt, Mail, FileDown, FileText as FileTextIcon, Wand2, Link2, Copy, Split } from "lucide-react";
import { InvoiceModal, PrintView, fmtEUR, statusBadge } from "./InvoiceCommon";
import EmailDialog from "./EmailDialog";
import GenerateDevisDialog from "./GenerateDevisDialog";
import SplitBySiteDialog from "./SplitBySiteDialog";
import { SiteNames, useClientSiteFilter, ClientSiteFilterBar } from "./ListFilters";

function SignLinkDialog({ devis, onClose }) {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await http.post(`/devis/${devis.id}/sign-link`);
        const publicPath = r.data.path; // /sign/devis/<token>
        setUrl(`${window.location.origin}${publicPath}`);
      } finally { setLoading(false); }
    })();
  }, [devis.id]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(()=>setCopied(false), 1800); }
    catch { /* clipboard indisponible */ }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg border border-slate-200 p-5" onClick={(e)=>e.stopPropagation()} data-testid="sign-link-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Lien de signature — {devis.number}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">✕</button>
        </div>
        <p className="text-xs text-slate-600 mb-3">Envoyez ce lien à votre client. Il pourra consulter le devis et le signer en ligne (bon pour accord). Le devis passera automatiquement en statut <b>Accepté</b>.</p>
        {loading ? (
          <div className="text-sm text-slate-500 py-4 text-center">Génération du lien…</div>
        ) : (
          <div className="flex items-center gap-2">
            <input readOnly value={url} data-testid="sign-link-url" className="flex-1 font-mono text-xs border border-slate-300 rounded px-2 py-2 bg-slate-50" />
            <button onClick={copy} data-testid="sign-link-copy" className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-white rounded-md" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Copy className="h-3.5 w-3.5" /> {copied ? "Copié !" : "Copier"}
            </button>
          </div>
        )}
        <div className="mt-4 text-[11px] text-slate-500 border-t pt-3">
          Astuce : envoyez ce lien par email ou messagerie. Aucune connexion requise pour le client.
        </div>
      </div>
    </div>
  );
}

export default function Devis() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [signLink, setSignLink] = useState(null);
  const [company, setCompany] = useState(null);
  const [filter, setFilter] = useState("all");
  const [splitting, setSplitting] = useState(null);
  const { lastEvent } = useRealtime();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("open"), t = id && items.find((x) => x.id === id);
    if (t) { setEditing(t); setSearchParams({}, { replace: true }); }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = async () => {
    const [d, c] = await Promise.all([http.get("/devis"), http.get("/settings/company")]);
    setItems(d.data); setCompany(c.data);
    const openId = searchParams.get("open");
    const target = openId && d.data.find((x) => x.id === openId);
    if (target) { setEditing(target); setSearchParams({}, { replace: true }); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "devis_changed" || lastEvent?.event === "company_changed") load(); }, [lastEvent]);

  const setStatus = async (id, status) => { await http.patch(`/devis/${id}/status`, { status }); load(); };
  const convert = async (id) => { if (window.confirm("Convertir en facture ?")) { await http.post(`/devis/${id}/to-facture`); alert("Facture créée !"); load(); } };

  const cs = useClientSiteFilter(items);
  const filtered = items.filter(i => (filter === "all" || i.status === filter) && cs.match(i));

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="devis-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Devis</h1>
          <p className="text-sm text-slate-600">{items.length} devis au total</p>
        </div>
        <div className="flex gap-2">
          <a data-testid="export-devis-xlsx" href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/export.xlsx`} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold"><FileDown className="h-4 w-4"/> Exporter Excel</a>
          <button data-testid="generate-from-planning" onClick={()=>setGenerating(true)} className="inline-flex items-center gap-2 border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 px-4 py-2 rounded-md text-sm font-semibold"><Wand2 className="h-4 w-4"/> Depuis planning</button>
          <button data-testid="add-devis" onClick={()=>setEditing({})} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4"/> Nouveau devis</button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 text-xs font-bold uppercase tracking-widest">
        {["all","draft","sent","accepted","rejected"].map(f => (
          <button key={f} data-testid={`filter-status-${f}`} onClick={()=>setFilter(f)} className={`px-3 py-1.5 rounded-md border transition-colors ${filter===f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>{{all:"Tous",draft:"Brouillon",sent:"Envoyé",accepted:"Accepté",rejected:"Refusé",paid:"Payée",overdue:"En retard",cancelled:"Annulée"}[f] || f}</button>
        ))}
      </div>
      <ClientSiteFilterBar f={cs} prefix="devis" />

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-4 py-3">Numéro</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Site(s)</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Total TTC</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(d => (
              <tr key={d.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono font-semibold text-slate-900">{d.number}</td>
                <td className="px-4 py-3 text-slate-600">{d.date}</td>
                <td className="px-4 py-3 font-semibold">{d.client_name}</td>
                <td className="px-4 py-3" data-testid={`devis-sites-${d.id}`}><SiteNames doc={d} /></td>
                <td className="px-4 py-3">{statusBadge("devis", d.status)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{fmtEUR(d.total_ttc)}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <a data-testid={`devis-pdf-${d.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/${d.id}/export.pdf`} title="Télécharger PDF" className="inline-block p-1.5 rounded hover:bg-slate-100"><FileTextIcon className="h-4 w-4 text-slate-600"/></a>
                  <button onClick={()=>setPrinting(d)} title="Aperçu" className="p-1.5 rounded hover:bg-slate-100"><Printer className="h-4 w-4 text-slate-600"/></button>
                  <button data-testid={`devis-email-${d.id}`} onClick={()=>setEmailing(d)} title="Envoyer par email" className="p-1.5 rounded hover:bg-sky-50"><Mail className="h-4 w-4 text-sky-600"/></button>
                  {d.status !== "accepted" && d.status !== "cancelled" && d.status !== "rejected" && (
                    <button data-testid={`devis-sign-link-${d.id}`} onClick={()=>setSignLink(d)} title="Lien de signature client" className="p-1.5 rounded hover:bg-amber-50"><Link2 className="h-4 w-4 text-amber-600"/></button>
                  )}
                  {d.status === "draft" && <button onClick={()=>setStatus(d.id, "sent")} title="Marquer envoyé" className="p-1.5 rounded hover:bg-sky-50"><Send className="h-4 w-4 text-sky-600"/></button>}
                  {d.status === "sent" && <>
                    <button onClick={()=>setStatus(d.id, "accepted")} title="Accepter" className="p-1.5 rounded hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4 text-emerald-600"/></button>
                    <button onClick={()=>setStatus(d.id, "rejected")} title="Refuser" className="p-1.5 rounded hover:bg-rose-50"><XCircle className="h-4 w-4 text-rose-600"/></button>
                  </>}
                  {d.status === "accepted" && <button data-testid={`devis-to-facture-${d.id}`} onClick={()=>convert(d.id)} title="Convertir en facture" className="p-1.5 rounded hover:bg-indigo-50"><Receipt className="h-4 w-4 text-indigo-600"/></button>}
                  {d.status === "accepted" && d.multi_site && d.sites?.length > 0 && <button data-testid={`devis-split-${d.id}`} onClick={()=>setSplitting(d)} title={d.split_invoiced?.length ? `Déjà facturé par site : ${d.split_invoiced.join(", ")}` : "1 facture par site"} className="p-1.5 rounded hover:bg-indigo-50 relative"><Split className="h-4 w-4 text-indigo-600"/>{d.split_invoiced?.length > 0 && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500"/>}</button>}
                  <button onClick={()=>setEditing(d)} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4"/></button>
                  <button data-testid={`devis-delete-${d.id}`} onClick={async()=>{ if(askDelete("Supprimer ?")){ await http.delete(`/devis/${d.id}`); load();}}} title={delTitle()} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-4 w-4 text-rose-600"/></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-10 text-sm">Aucun devis.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <InvoiceModal kind="devis" value={editing} company={company} onClose={()=>setEditing(null)} onSaved={load}/>}
      {printing && <PrintView kind="devis" doc={printing} company={company} onClose={()=>setPrinting(null)}/>}
      {emailing && <EmailDialog kind="devis" doc={emailing} onClose={()=>setEmailing(null)} onSent={load}/>}
      {generating && <GenerateDevisDialog onClose={()=>{ setGenerating(false); load(); }}/>}
      {signLink && <SignLinkDialog devis={signLink} onClose={()=>setSignLink(null)}/>}
      {splitting && <SplitBySiteDialog devis={splitting} onClose={()=>setSplitting(null)} onDone={load}/>}
    </div>
  );
}
